// @flow
import type { Effect } from 'redux-saga';
import type { EditAction, NewAction } from '~/core/types/beekeeper';
import type { Action } from '~/core/types';
import type { RemoveAction } from './types';

import { put, call, select, takeEvery, takeLatest } from 'redux-saga/effects';
import moment from 'moment-timezone';
import { noop } from 'lodash/fp';
import handleSaga from '~/core/state/handle-saga';
import handleApi from '~/core/state/handle-api';
import { getConfig } from '~/core/state/config/selectors';
import { actions as actionsApi } from '~/core/api';
import {
    ATTEMPT_CREATE_ACTION,
    ATTEMPT_EDIT_ACTION,
    ATTEMPT_REMOVE_ACTION,
    GET_ACTIONS,
    GET_TEMPLATES,
    ATTEMPT_TRIGGER_QUICK_ACTION
} from './constants';
import {
    editAction,
    endAttempt,
    makeEndAttemptCreateAction,
    receiveActions,
    receiveTemplates,
    removeActionFromList,
    removeActionFromDeleteList,
    triggerQuickAction
} from './actions';
import { pollNodeExists } from '~/core/state/honeycomb/actions';
import { getCurrentTime, getTimezone } from '~/core/state/hub/selectors';
import { requestAlert } from '~/core/state/alerts/actions';
import { types as alertTypes } from '~/core/constants/alerts';
import { generalErrorMessages } from '~/core/constants/errors';
import { isQuickAction } from '~/core/state/actions/selectors';
import { dashboardRemoveProducts, saveDashboardEdit } from '~/core/state/dashboard/actions';
import { getDashboardItemPositionById } from '~/core/state/dashboard/selectors';

/*********************
 *   GET TEMPLATES   *
 *********************/

export function* getTemplates(): Generator<noop | Effect, *, *> {
    const templatesVersion = yield select(getConfig, 'actions:templatesVersion');
    const [response = {}, error] = yield call(handleApi(actionsApi.getTemplates), templatesVersion);
    const is404 = error && error.status === 404;
    yield put(receiveTemplates(error ? (is404 ? response : error) : response));
}

function* watchGetTemplates(): Generator<Effect, *, *> {
    yield takeLatest(GET_TEMPLATES, handleSaga(getTemplates));
}

/*******************
 *   GET ACTIONS   *
 *******************/

export function* getListActions(): Generator<noop | Effect, *, *> {
    const [response = {}, error] = yield call(handleApi(actionsApi.getActions));
    const is404 = error && error.status === 404;
    yield put(receiveActions(error ? (is404 ? response : error) : response));
}

function* watchGetActions(): Generator<Effect, *, *> {
    yield takeLatest(GET_ACTIONS, handleSaga(getListActions));
}

/*********************
 *   CREATE ACTION   *
 *********************/

export function* attemptCreateHiveAction({ payload: action }: Action<NewAction>): Generator<noop | Effect, *, *> {
    const [response, error] = yield call(handleApi(actionsApi.createHiveAction), action);

    if (error) {
        yield put(endAttempt(error));
        return;
    }

    const pollId = response.poll;
    const hubTime = yield select(getCurrentTime);
    const timeZone = yield select(getTimezone);
    const now = moment.tz(hubTime, timeZone).valueOf();

    yield put(
        pollNodeExists({
            id: pollId,
            nextAction: makeEndAttemptCreateAction({ newAction: action, created: now })
        })
    );
}

function* watchCreateAction(): Generator<Effect, *, *> {
    yield takeEvery(ATTEMPT_CREATE_ACTION, handleSaga(attemptCreateHiveAction));
}

/*********************
 *   EDIT ACTION   *
 *********************/

export function* attemptEditAction({ payload: action }: Action<EditAction>): Generator<noop | Effect, *, *> {
    if (!action) {
        yield put(endAttempt(new Error('Error: No action to be edited')));
        return;
    }

    const [, error] = yield call(handleApi(actionsApi.editAction), action, action.id);
    if (error) {
        yield put(endAttempt(error));
        yield put(requestAlert(generalErrorMessages.tryAgain, alertTypes.ERROR));
        return;
    }

    yield put(endAttempt());
    yield put(editAction(action));
}

function* watchEditAction(): Generator<Effect, *, *> {
    yield takeEvery(ATTEMPT_EDIT_ACTION, handleSaga(attemptEditAction));
}

/*********************
 *   REMOVE ACTION   *
 *********************/

export function* attemptRemoveAction({ payload: actionId }: RemoveAction): Generator<noop | Effect, *, *> {
    const [, error] = yield call(handleApi(actionsApi.removeAction), actionId);

    if (error) {
        yield put(endAttempt(error));
        yield put(removeActionFromDeleteList(actionId));
        yield put(requestAlert(generalErrorMessages.tryAgain, alertTypes.ERROR));
        return;
    }
    //Delete actionId from dashboard customisation if it exists
    const deleteFromDashboard = yield select(isQuickAction, { actionId });
    if (deleteFromDashboard) {
        const existsOnDashboard = yield select(getDashboardItemPositionById, { id: actionId });
        if (existsOnDashboard) {
            yield put(dashboardRemoveProducts({ ids: [actionId] }));
            yield put(saveDashboardEdit());
        }
    }

    yield put(endAttempt());
    yield put(removeActionFromDeleteList(actionId));
    yield put(removeActionFromList(actionId));
}

function* watchRemoveAction(): Generator<Effect, *, *> {
    yield takeEvery(ATTEMPT_REMOVE_ACTION, handleSaga(attemptRemoveAction));
}

export function* attemptTriggerQuickAction({ payload: actionId }: RemoveAction): Generator<noop | Effect, *, *> {
    const [, error] = yield call(handleApi(actionsApi.triggerQuickAction), actionId);

    if (error) {
        yield put(triggerQuickAction(new Error(actionId)));
        yield put(requestAlert(generalErrorMessages.tryAgain, alertTypes.ERROR));
        return;
    }
    yield put(triggerQuickAction(actionId));
}

function* watchTriggerQuickAction(): Generator<Effect, *, *> {
    yield takeEvery(ATTEMPT_TRIGGER_QUICK_ACTION, handleSaga(attemptTriggerQuickAction));
}

/*********************/

export default [
    watchCreateAction,
    watchEditAction,
    watchGetActions,
    watchGetTemplates,
    watchRemoveAction,
    watchTriggerQuickAction
];



// WEBPACK FOOTER //
// ./src/core/state/actions/sagas.js